import { z } from 'zod';
// =============================================================================
// Checkout (M2.3 → M2.4/2.5 consolidado: Stripe Checkout hospedado)
// =============================================================================
export const profitVariantSchema = z.enum(['PROFIT_ONE', 'PROFIT_PRO']);
/**
 * Modo de pagamento do plano:
 * - CARD_MP: cartão parcelado 1x-12x via Mercado Pago (Checkout Transparente).
 *   A empresa recebe o valor CHEIO à vista; de 2x em diante os juros são do
 *   comprador (tabela do próprio MP). É o modo padrão do cartão em BRL.
 * - PIX_ONE_TIME: Pix à vista via Mercado Pago (QR + polling).
 * - CARD_12X: LEGADO — Stripe Subscription de 12 cobranças mensais. Mantido no
 *   enum só pra não quebrar requisições de bundles antigos durante o deploy;
 *   o front novo não envia mais este modo.
 * - CARD_USD_STRIPE: cartão internacional à vista, cobrado em DÓLAR pelo Stripe.
 *   É o caminho de quem está fora do Brasil (Pix e parcelamento não servem lá
 *   fora). Só existe para plano com preço em USD — plano em BRL é recusado.
 */
export const paymentModeSchema = z.enum(['CARD_MP', 'CARD_12X', 'PIX_ONE_TIME', 'CARD_USD_STRIPE']);
/** Nº máximo de parcelas oferecidas no cartão via Mercado Pago. */
export const MP_MAX_INSTALLMENTS = 12;
// =============================================================================
// Cartão 12x com repasse de taxas (gross-up por parcela)
// =============================================================================
/**
 * Repasse padrão das taxas do cartão POR PARCELA: 3,99% + R$0,39 (cartão
 * nacional) + 0,7% Stripe Billing = 4,69% + R$0,39. Os valores reais vêm das
 * GlobalSettings (`checkout.card_fee_percent` / `checkout.card_fee_fixed_cents`)
 * — estes defaults servem de fallback e alimentam os displays estáticos dos
 * sites (cards de plano) antes do /checkout/config responder.
 */
export const CARD_FEE_PERCENT_DEFAULT = 4.69;
export const CARD_FEE_FIXED_CENTS_DEFAULT = 39;
/** Nº de parcelas do "12x" (assinatura de 12 cobranças mensais no Stripe). */
export const CARD_INSTALLMENTS = 12;
/**
 * Calcula a parcela mensal com o repasse das taxas do cartão (gross-up).
 *
 * Por parcela: ceil((ceil(netTotal / installments) + feeFixedCents) / (1 - feePercent/100)).
 * Arredondamentos SEMPRE pra cima (ceil) — o repasse nunca fica abaixo do custo.
 *
 * Função pura e compartilhada: a API usa pra criar o Stripe Price dinâmico e
 * os fronts usam como fallback local nos displays (cards/resumo do checkout).
 */
export function grossUpInstallments(netTotalCents, opts) {
    const installments = opts.installments ?? CARD_INSTALLMENTS;
    const netInstallment = Math.ceil(netTotalCents / installments);
    const installmentCents = Math.ceil((netInstallment + opts.feeFixedCents) / (1 - opts.feePercent / 100));
    return { installmentCents, totalCents: installmentCents * installments };
}
// Endereço completo (estilo MIDE)
export const addressSchema = z.object({
    zip: z.string().min(3).max(20),
    street: z.string().min(2).max(200),
    number: z.string().min(1).max(20),
    complement: z.string().max(120).optional(),
    neighborhood: z.string().min(2).max(120),
    city: z.string().min(2).max(120),
    state: z.string().min(1).max(80),
    country: z.string().min(2).max(80),
});
// Dados pessoais completos coletados no AccountModal
export const personalDataSchema = z.object({
    fullName: z.string().min(2).max(120),
    gender: z.enum(['MALE', 'FEMALE', 'OTHER']).optional(),
    documentType: z.enum(['CPF', 'RG', 'PASSPORT']).optional(),
    documentNumber: z.string().min(3).max(40).optional(),
    birthDate: z.string().optional(), // ISO YYYY-MM-DD
    nationality: z.string().min(2).max(80).optional(),
    phone: z.string().min(8).max(40).optional(),
});
export const startCheckoutInputSchema = z.object({
    planCode: z.string().min(2),
    marketCode: z.enum(['B3', 'FOREX']),
    // Dados da conta (cria user se não existir)
    email: z.string().email().toLowerCase(),
    // OPCIONAL de propósito: só é usado pra CRIAR usuário novo. Quem já tem conta
    // (comprando logado, 2ª compra) não precisa reenviar — o servidor já tem o
    // nome no perfil. Exigir aqui obrigava o cliente logado a refazer o cadastro
    // inteiro, e ainda batia em "email já cadastrado".
    fullName: z.string().min(2).max(120).optional(),
    // Senha definida pelo trader no AccountModal (opcional — se ausente, gera temporária)
    password: z.string().min(8).max(128).optional(),
    // Dados pessoais e endereço completos (opcionais — se vierem, populam o Profile)
    personal: personalDataSchema.partial().optional(),
    address: addressSchema.optional(),
    // B3 only: escolha de plataforma. Forex sempre BlackArrow.
    profitVariant: profitVariantSchema.optional(),
    // Upsell saque diário (legado — mantido pra compat com bundle antigo)
    dailyWithdrawalUpsell: z.boolean().default(false),
    // Códigos de addons selecionados (Day Off, Boost, Restart, etc — Pedro
    // pediu estrutura expansível pra campanhas, admin cria via
    // /admin/checkout-addons)
    addonCodes: z.array(z.string()).default([]),
    // Aceite dos termos (versão registrada no terms_acceptances)
    termsVersion: z.string().min(1),
    acceptTerms: z.literal(true),
    // Cupom opcional
    couponCode: z.string().optional(),
    // Modo de pagamento. Default CARD_MP (cartão parcelado via Mercado Pago).
    paymentMode: paymentModeSchema.default('CARD_MP'),
});
// =============================================================================
// Cartão via Mercado Pago (Checkout Transparente)
// =============================================================================
/**
 * Pagamento com cartão de um checkout JÁ iniciado (/checkout/start criou a
 * Subscription pendente). O browser tokeniza o cartão com o MercadoPago.js e
 * manda só referências — NUNCA o número do cartão, e NUNCA o valor (o servidor
 * recalcula a partir da Subscription).
 */
export const payCardInputSchema = z.object({
    subscriptionId: z.string().min(1),
    /** Token de uso único gerado pelo MercadoPago.js no browser. */
    token: z.string().min(1),
    /** Parcelas escolhidas pelo comprador (validadas contra a tabela do MP). */
    installments: z.coerce.number().int().min(1).max(MP_MAX_INSTALLMENTS),
    /** Bandeira resolvida pelo SDK a partir do BIN (ex.: 'visa', 'master'). */
    paymentMethodId: z.string().min(1).max(40),
    /** Emissor resolvido pelo SDK (melhora a taxa de aprovação). */
    issuerId: z.string().max(40).optional(),
    /** CPF do titular do cartão (o MP praticamente exige no Brasil). */
    identificationNumber: z.string().max(20).optional(),
    /**
     * Device fingerprint do MP (antifraude — melhora aprovação). O SDK v2 injeta
     * sozinho em window.MP_DEVICE_SESSION_ID e a string é LONGA (passava fácil de
     * 200 e derrubava o pagamento inteiro na validação). Como é dado opcional de
     * telemetria, o limite aqui é generoso.
     */
    // .catch(undefined): se vier fora do formato, DESCARTA o campo em vez de
    // recusar o pagamento. Telemetria opcional não pode bloquear uma venda.
    deviceId: z.string().max(2000).optional().catch(undefined),
});
//# sourceMappingURL=checkout.js.map